import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Film, Users, BarChart3, Settings, Plus, Trash2, Edit, Eye,
  TrendingUp, Layers, Upload, X, Save, Search, Play, Tv,
  UserX, UserCheck, KeyRound, ChevronLeft, ChevronRight,
  Tag, Globe, Star, CreditCard, Check, Ticket, Calendar, Percent,
  Volume2, Joystick, ChevronUp, ChevronDown, RefreshCw, Sparkles, SlidersHorizontal, AlertTriangle, Scissors, GripVertical
} from 'lucide-react';
import { TrimEditor, fmtTrim } from './TrimEditor';
import { AiTrailerButton, AiTrailerBulkButton } from './AiTrailer';
import { ConfirmModal } from './TrashTab';
import FilterTimelineEditor from '../FilterTimelineEditor';
import { LanguageStats } from '../analytics/LanguageStats';
import {
  API, CATEGORIES_LIST, ALL_COUNTRIES, MEDIA_SPECS, LANGUAGES, TYPE_LABELS, TYPE_LABELS_PLURAL, uploadFileToS3, FilterBadge,
  formatFileSize, TimecodeInput, CountryPicker, FilterSelect, LoadingSpinner as SharedLoadingSpinner, validateMediaFile
} from './shared';
import { EpisodePreview, EpisodePreviewModal } from './EpisodePreview';
import { LoadingSpinner } from './core';

export function AdsTab({ theme }) {
  const light = theme === 'light';
  const inputCls = light ? 'bg-gray-100 text-gray-800 border border-gray-200' : 'bg-white/5 text-white border border-white/10';
  const hintCls = light ? 'text-gray-400' : 'text-white/30';

  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [audienceAd, setAudienceAd] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', advertiser: '', video_url: '', image_url: '', format: 'video', click_url: '', duration: 15, type: 'both', active: true, start_date: '', end_date: '' });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const fetchAds = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/promo-spots`, { withCredentials: true });
      setAds(data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchAds(); }, []);

  const resetForm = () => {
    setForm({ name: '', advertiser: '', video_url: '', image_url: '', format: 'video', click_url: '', duration: 15, type: 'both', active: true, start_date: '', end_date: '' });
    setEditing(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/admin/promo-spots/${editing}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/promo-spots`, form, { withCredentials: true });
      }
      resetForm();
      fetchAds();
    } catch (err) { alert(err.response?.data?.detail || `Erreur lors de l'enregistrement (code ${err.response?.status || 'réseau'})`); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette publicité ?')) return;
    try {
      await axios.delete(`${API}/admin/promo-spots/${id}`, { withCredentials: true });
      fetchAds();
    } catch (err) { alert(err.response?.data?.detail || `Erreur lors de la suppression (code ${err.response?.status || 'réseau'})`); }
  };

  const startEdit = (ad) => {
    setForm({ name: ad.name, advertiser: ad.advertiser, video_url: ad.video_url, image_url: ad.image_url || '', format: ad.format || 'video', click_url: ad.click_url || '', duration: ad.duration, type: ad.type, active: ad.active, start_date: ad.start_date || '', end_date: ad.end_date || '' });
    setEditing(ad.id);
    setShowForm(true);
  };

  const handleUpload = async (e, field = 'video_url') => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      const { data: presigned } = await axios.post(`${API}/upload/presign`, { filename: file.name, content_type: file.type }, { withCredentials: true });
      if (presigned.mode === 'local' || presigned.upload_url === '__local__') {
        const fd = new FormData(); fd.append('file', file);
        const { data: localRes } = await axios.post(`${API}/upload/local`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress: (evt) => setUploadProgress(Math.round((evt.loaded * 100) / (evt.total || 1))) });
        setForm(p => ({ ...p, [field]: localRes.file_url }));
      } else {
        await axios.put(presigned.upload_url, file, { headers: { 'Content-Type': file.type }, onUploadProgress: (evt) => setUploadProgress(Math.round((evt.loaded * 100) / (evt.total || 1))) });
        setForm(p => ({ ...p, [field]: presigned.file_url }));
      }
      setUploadProgress(100);
      // Auto-detect duration (video only)
      if (field === 'video_url') {
        const url = URL.createObjectURL(file);
        const v = document.createElement('video'); v.preload = 'metadata';
        v.onloadedmetadata = () => { setForm(p => ({ ...p, duration: Math.round(v.duration) })); URL.revokeObjectURL(url); };
        v.src = url;
      }
    } catch (err) { alert('Upload échoué: ' + (err.message || '')); }
    finally { setUploading(false); setTimeout(() => setUploadProgress(null), 1500); }
  };

  const sf = (k, v) => setForm(p => ({ ...p, [k]: v }));

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-xl font-bold ${light ? 'text-gray-900' : 'text-white'}`}>Publicités</h2>
          <p className={`text-sm mt-1 ${hintCls}`}>{ads.length} publicité{ads.length !== 1 ? 's' : ''} · {ads.filter(a => a.active).length} active{ads.filter(a => a.active).length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }} className="flex items-center gap-2 bg-[#E9641A] hover:bg-[#FE975E] text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors" data-testid="add-ad-btn">
          <Plus size={14} /> Ajouter une pub
        </button>
      </div>

      {showForm && (
        <div className={`rounded-xl border p-5 space-y-4 ${light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5'}`}>
          <h3 className={`text-sm font-bold ${light ? 'text-gray-800' : 'text-white'}`}>{editing ? 'Modifier la publicité' : 'Nouvelle publicité'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={`block text-[10px] mb-1 ${hintCls}`}>Nom de la campagne</label>
              <input value={form.name} onChange={e => sf('name', e.target.value)} placeholder="Ex: Promo Hajj 2026" className={`w-full rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`} />
            </div>
            <div>
              <label className={`block text-[10px] mb-1 ${hintCls}`}>Annonceur</label>
              <input value={form.advertiser} onChange={e => sf('advertiser', e.target.value)} placeholder="Ex: Agence Omra Travel" className={`w-full rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`} />
            </div>
          </div>
          <div>
            <label className={`block text-[10px] mb-1 ${hintCls}`}>Format</label>
            <select value={form.format} onChange={e => sf('format', e.target.value)} className={`w-full rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`} data-testid="ad-format-select">
              <option value="video">Vidéo (avant / pendant le film)</option>
              <option value="pause">Écran de pause TV (image fixe)</option>
            </select>
          </div>
          {form.format === 'pause' ? (
            <div>
              <label className={`block text-[10px] mb-1 ${hintCls}`}>Image de la pub (paysage 16:9)</label>
              <div className="flex gap-2 items-center">
                <input value={form.image_url} onChange={e => sf('image_url', e.target.value)} placeholder="URL de l'image ou upload..." className={`flex-1 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`} data-testid="ad-image-url-input" />
                <label className={`flex items-center px-3 py-2 rounded-lg text-xs cursor-pointer transition-colors ${light ? 'bg-gray-100 text-gray-500 hover:text-gray-700' : 'bg-white/8 text-white/40 hover:text-white'}`}>
                  <Upload size={12} className="mr-1" /> Upload
                  <input type="file" accept="image/*" className="hidden" onChange={e => handleUpload(e, 'image_url')} data-testid="ad-image-upload-input" />
                </label>
              </div>
              {form.image_url && <img src={form.image_url} alt="" className="mt-2 h-20 rounded-lg object-cover" data-testid="ad-image-preview" />}
            </div>
          ) : (
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer?.files?.[0]; if (f) handleUpload({ target: { files: [f] } }); }}
            className={`rounded-lg transition-all ${dragOver ? 'ring-2 ring-[#E9641A] bg-[#E9641A]/5 p-2' : ''}`}
          >
            <label className={`block text-[10px] mb-1 ${hintCls}`}>Vidéo publicitaire</label>
            {dragOver ? (
              <div className={`flex items-center justify-center h-12 rounded-lg border-2 border-dashed border-[#E9641A] ${light ? 'bg-[#E9641A]/5' : 'bg-[#E9641A]/10'}`}>
                <p className="text-[#E9641A] text-xs font-semibold">Déposez la vidéo ici</p>
              </div>
            ) : (
              <div className="flex gap-2">
                <input value={form.video_url} onChange={e => sf('video_url', e.target.value)} placeholder="URL ou glisser-déposer..." className={`flex-1 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`} />
                <label className={`flex items-center px-3 py-2 rounded-lg text-xs cursor-pointer transition-colors ${light ? 'bg-gray-100 text-gray-500 hover:text-gray-700' : 'bg-white/8 text-white/40 hover:text-white'}`}>
                  <Upload size={12} className="mr-1" /> Upload
                  <input type="file" accept="video/*" className="hidden" onChange={handleUpload} />
                </label>
              </div>
            )}
            {uploadProgress !== null && (
              <div className="mt-1">
                <div className={`h-1 rounded-full ${light ? 'bg-gray-200' : 'bg-white/10'}`}><div className={`h-full rounded-full transition-all ${uploadProgress === 100 ? 'bg-green-400' : 'bg-[#E9641A]'}`} style={{ width: `${uploadProgress}%` }} /></div>
              </div>
            )}
          </div>
          )}
          <div>
            <label className={`block text-[10px] mb-1 ${hintCls}`}>Lien au clic (optionnel)</label>
            <input value={form.click_url} onChange={e => sf('click_url', e.target.value)} placeholder="https://annonceur.com/promo" className={`w-full rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {form.format !== 'pause' && (<>
            <div>
              <label className={`block text-[10px] mb-1 ${hintCls}`}>Durée</label>
              <select value={form.duration} onChange={e => sf('duration', parseInt(e.target.value))} className={`w-full rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`}>
                <option value={10}>10 secondes</option>
                <option value={15}>15 secondes</option>
                <option value={20}>20 secondes</option>
                <option value={30}>30 secondes</option>
              </select>
            </div>
            <div>
              <label className={`block text-[10px] mb-1 ${hintCls}`}>Position</label>
              <select value={form.type} onChange={e => sf('type', e.target.value)} className={`w-full rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`}>
                <option value="pre-roll">Avant le film</option>
                <option value="mid-roll">Pendant le film</option>
                <option value="both">Les deux</option>
              </select>
            </div>
            </>)}
            <div>
              <label className={`block text-[10px] mb-1 ${hintCls}`}>Date début</label>
              <input type="date" value={form.start_date} onChange={e => sf('start_date', e.target.value)} className={`w-full rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`} />
            </div>
            <div>
              <label className={`block text-[10px] mb-1 ${hintCls}`}>Date fin</label>
              <input type="date" value={form.end_date} onChange={e => sf('end_date', e.target.value)} className={`w-full rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`} />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className={`flex items-center gap-2 text-xs cursor-pointer ${light ? 'text-gray-600' : 'text-white/60'}`}>
              <input type="checkbox" checked={form.active} onChange={e => sf('active', e.target.checked)} className="accent-[#E9641A]" /> Active
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={resetForm} className={`px-4 py-2 text-xs rounded-lg ${light ? 'bg-gray-100 text-gray-500' : 'bg-white/5 text-white/50'}`}>Annuler</button>
            <button onClick={handleSave} className="px-4 py-2 text-xs font-bold rounded-lg bg-[#E9641A] text-white hover:bg-[#FE975E] transition-colors" data-testid="save-ad-btn">{editing ? 'Modifier' : 'Créer'}</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {ads.map(ad => (
          <div key={ad.id} className={`flex items-center gap-4 rounded-xl p-4 border transition-colors ${light ? 'bg-white border-gray-200 hover:border-gray-300' : 'bg-white/3 border-white/5 hover:border-white/10'}`}>
            <div className={`w-2 h-12 rounded-full flex-none ${ad.active ? 'bg-green-400' : 'bg-white/20'}`} />
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold truncate ${light ? 'text-gray-800' : 'text-white'}`}>{ad.name || 'Sans nom'}</p>
              <p className={`text-[11px] ${hintCls}`}>{ad.advertiser || '—'} · {ad.format === 'pause' ? 'Écran de pause TV (image)' : `${ad.duration}s · ${ad.type === 'pre-roll' ? 'Avant' : ad.type === 'mid-roll' ? 'Pendant' : 'Avant + Pendant'}`}</p>
            </div>
            <div className="text-right flex-none">
              <p className={`text-sm font-mono ${light ? 'text-gray-700' : 'text-white/80'}`}>{(ad.impressions || 0).toLocaleString()} <span className={`text-[10px] ${hintCls}`}>vues</span></p>
              <p className={`text-[11px] ${hintCls}`}>{(ad.clicks || 0).toLocaleString()} clics</p>
            </div>
            <div className="flex gap-1 flex-none">
              <button onClick={() => setAudienceAd(ad)} title="Audience détaillée" data-testid={`ad-audience-btn-${ad.id}`}
                className={`p-2 rounded-lg text-xs text-[#E9641A] ${light ? 'hover:bg-orange-50' : 'hover:bg-[#E9641A]/10'}`}><Users size={14} /></button>
              <button onClick={() => startEdit(ad)} className={`p-2 rounded-lg text-xs ${light ? 'hover:bg-gray-100 text-gray-400' : 'hover:bg-white/5 text-white/30'}`}><Edit size={14} /></button>
              <button onClick={() => handleDelete(ad.id)} className={`p-2 rounded-lg text-xs text-red-400 ${light ? 'hover:bg-red-50' : 'hover:bg-red-400/10'}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {ads.length === 0 && <p className={`text-center text-sm py-8 ${hintCls}`}>Aucune publicité. Cliquez sur "Ajouter une pub" pour commencer.</p>}
      </div>
      {audienceAd && <AdAudienceModal ad={audienceAd} light={light} onClose={() => setAudienceAd(null)} />}
    </div>
  );
}

/* ─── AUDIENCE D'UNE PUB ─── */
function AdAudienceModal({ ad, light, onClose }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/telemetry/ads`, { withCredentials: true })
      .then(r => setStats((r.data.ads || []).find(a => a.ad_id === ad.id) || { empty: true }))
      .catch(() => setStats({ empty: true }));
  }, [ad.id]);
  const hint = light ? 'text-gray-400' : 'text-white/35';
  const main = light ? 'text-gray-900' : 'text-white';
  const card = light ? 'bg-gray-50 border-gray-200' : 'bg-white/[0.03] border-white/5';
  const s = stats || {};
  const kpis = [
    ['Diffusions', s.views, 'text-[#E9641A]'], ['Spectateurs uniques', s.unique_viewers, 'text-white'],
    ['Clics', s.clicks, 'text-teal-400'], ['Taux de clic', s.views ? `${s.ctr}%` : '—', 'text-teal-400'],
    ['Vues complètes', s.completes, 'text-emerald-400'],
    ['Début de film', s.preroll, 'text-sky-400'], ['Milieu de film', s.midroll, 'text-purple-400'],
  ];
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="ad-audience-modal">
      <div className={`rounded-xl border w-full max-w-3xl max-h-[88vh] overflow-y-auto p-6 ${light ? 'bg-white border-gray-200' : 'bg-[#1a0e2e] border-white/10'}`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className={`text-lg font-bold flex items-center gap-2 ${main}`}><Users size={18} className="text-[#E9641A]" /> Audience — {ad.name || 'Publicité'}</h3>
          <button onClick={onClose} className={light ? 'text-gray-400 hover:text-gray-700' : 'text-white/40 hover:text-white'} data-testid="ad-audience-close"><X size={18} /></button>
        </div>
        <p className={`text-xs mb-5 ${hint}`}>Qui a vu cette pub, combien de fois, à quel moment du film, et qui a cliqué. Suivi détaillé démarré avec la télémétrie lecteur (30 jours d'historique).</p>
        {!stats ? <LoadingSpinner /> : stats.empty ? (
          <p className={`text-center text-sm py-8 ${hint}`}>Aucune diffusion suivie pour cette pub pour l'instant — les données apparaissent dès les prochaines lectures.</p>
        ) : (
          <>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-6">
              {kpis.map(([lbl, v, cls]) => (
                <div key={lbl} className={`rounded-lg border p-3 ${card}`}>
                  <p className={`text-xl font-extrabold ${cls === 'text-white' && light ? 'text-gray-900' : cls}`}>{v ?? 0}</p>
                  <p className={`text-[10px] mt-0.5 ${hint}`}>{lbl}</p>
                </div>
              ))}
            </div>
            <p className={`text-[10px] uppercase tracking-wider mb-2 ${hint}`}>Spectateurs ({(s.viewers || []).length})</p>
            <div className="space-y-1.5">
              {(s.viewers || []).map(v => (
                <div key={v.user_id} className={`rounded-lg border px-3 py-2 ${card}`} data-testid={`ad-viewer-${v.user_id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-semibold ${main}`}>{v.name || v.email}</span>
                    <span className={`text-[10px] ${hint}`}>{v.email}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-[#E9641A]/15 text-[#E9641A]">{v.views} vue{v.views > 1 ? 's' : ''}</span>
                    {v.preroll > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-400">Début ×{v.preroll}</span>}
                    {v.midroll > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-400">Milieu ×{v.midroll}</span>}
                    {v.completes > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">Complète ×{v.completes}</span>}
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${v.clicks > 0 ? 'bg-teal-500/20 text-teal-300' : light ? 'bg-gray-100 text-gray-400' : 'bg-white/8 text-white/35'}`}>
                      {v.clicks > 0 ? `A cliqué ×${v.clicks}` : "N'a pas cliqué"}
                    </span>
                    <span className={`text-[10px] ml-auto ${hint}`}>{new Date(v.last_ts).toLocaleString('fr-FR')}</span>
                  </div>
                  {(v.contents || []).length > 0 && (
                    <p className={`text-[10px] mt-1 ${hint}`}>Pendant : {v.contents.join(' · ')}</p>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
